/**
 * LayoutLoadingStore - MobX store для управління станом завантаження Layout
 * 
 * Використовується замість useState для реактивного управління станом
 * завантаження loadData без необхідності оборачувати весь Layout в observer
 */

import { makeAutoObservable, runInAction } from 'mobx'
import { LayoutTemplateConfig } from '../../Commands/Loaders/LayoutTemplateLoader'

export class LayoutLoadingStore {
    isLoadingData = true
    loadDataError: Error | null = null
    loadedData: any = null
    private currentTemplate: string | null = null
    
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    /**
     * Виконує loadData функцію з шаблону
     */
    async loadTemplateData(routeTemplate: string, forceReload = false): Promise<void> {
        // Пропускаємо якщо шаблон той самий і не форсується перезавантаження
        if (!forceReload && this.currentTemplate === routeTemplate && !this.isLoadingData) {
            return
        }

        let result: any = null
        try {
            runInAction(() => {
                this.isLoadingData = true
                this.loadDataError = null
                this.currentTemplate = routeTemplate
            })

            // Отримуємо компонент шаблону
            const TemplateComponent = LayoutTemplateConfig[routeTemplate]

            // Перевіряємо наявність loadData функції
            const loadDataFn = (TemplateComponent as any)?.loadData
            
            if (loadDataFn && typeof loadDataFn === 'function') {
                result = await loadDataFn()
            }

            runInAction(() => {
                this.loadedData = result
                this.isLoadingData = false
            })
        } catch (error) {
            console.error('[Layout] Error loading template data:', error)
            runInAction(() => {
                this.loadDataError = error instanceof Error ? error : new Error('Unknown error')
                this.isLoadingData = false
            })
        }
    }

    /**
     * Перезавантажує дані для поточного шаблону
     */
    async reloadData(): Promise<void> {
        if (this.currentTemplate) {
            await this.loadTemplateData(this.currentTemplate, true)
        }
    }

    /**
     * Скидає стан до початкового
     */
    reset(): void {
        this.isLoadingData = true
        this.loadDataError = null
        this.loadedData = null
        this.currentTemplate = null
    }
}

/**
 * Створює новий екземпляр LayoutLoadingStore
 */
export function createLayoutLoadingStore(): LayoutLoadingStore {
    return new LayoutLoadingStore()
}
